import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Button,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import * as Sentry from '@sentry/react'
import { withRouter } from 'react-router-dom/cjs/react-router-dom'
import { compose } from 'redux'
import { Translate } from 'react-localize-redux'
import backgroundImage from 'assets/images/cfg-background-image.jpg'
import cfgLogo from 'assets/images/city-coach.png'
import { Box, Grid } from '@mui/material'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import { getLocale } from 'utils/localstorage'
import urlConstructor from '../utils/urlConstructor'
import {
  createStripeCheckoutSession,
  createStripeSubscription,
} from '../routes/ClubSubscriptionPage/api'
import SubscriptionPlansData from '../SubscriptionPlansData'
import { Snackbars } from './index'

const PremiumContentDialog = ({
  open,
  onClose,
  lockedFeatureFlag,
  team,
  currentClub,
  stripeCurrency,
  match,
}) => {
  const {
    params: { seasonId },
  } = match

  const [isLoading, setIsLoading] = useState(false)
  const [snackbars, setSnackbars] = useState(false)
  const language = getLocale('locale')

  const features = [
    'premium-content.cfg.features.1',
    'premium-content.cfg.features.2',
    'premium-content.cfg.features.3',
    'premium-content.cfg.features.4',
    ...(language === 'de' ? ['premium-content.cfg.features.5'] : []),
  ]

  const successUrl =
    process.env.REACT_APP_URL + urlConstructor(team.uuid, seasonId)
  const cancelUrl =
    process.env.REACT_APP_URL + urlConstructor(team.uuid, seasonId)

  const userCanManageBilling = currentClub.can.includes('manage-billing')

  let title
  let message

  if (userCanManageBilling && lockedFeatureFlag === 'cb_premium_content') {
    title = <Translate id="premium-content.title" />
    message = <Translate id="premium-content.message" />
  } else if (
    userCanManageBilling &&
    lockedFeatureFlag === 'cfg_premium_content'
  ) {
    message = features
  } else {
    title = <Translate id="premium-content.title" />
    message = <Translate id="premium-content.no-permission.message" />
  }

  const handleSubscribeClick = (planUuid, amount) => {
    setIsLoading(true)
    setSnackbars(false)
    Sentry.addBreadcrumb({
      category: 'ui.click',
      message: 'User clicked the Addon Subscribe button',
      data: {
        amount,
        club_uuid: currentClub.uuid,
        subscription_plan_uuid: planUuid,
      },
    })

    // If the user is trying to subscribe to the free plan,
    // we need to create the subscription directly without a session.
    // Otherwise - create checkout session and redirect user to it.
    if (amount === 0) {
      createStripeSubscription(currentClub.uuid, {
        subscription_plan_uuid: planUuid,
      })
        .then(() => {
          // noinspection JSUnresolvedReference
          window.location.reload()
        })
        .catch((error) => {
          setIsLoading(false)
          setSnackbars({
            message: error.toString(),
            error: true,
          })
          Sentry.captureException(error)
        })
    } else {
      createStripeCheckoutSession(currentClub.uuid, {
        subscription_plan_uuid: planUuid,
        success_url: successUrl,
        cancel_url: cancelUrl,
      })
        .then((res) => {
          const { url } = res
          window.location.href = url
        })
        .catch((error) => {
          setIsLoading(false)
          setSnackbars({
            message: error.toString(),
            error: true,
          })
          Sentry.captureException(error)
        })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style:
          lockedFeatureFlag === 'cfg_premium_content'
            ? {
                backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.3)), url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                color: '#fff',
              }
            : {},
      }}
    >
      {snackbars && <Snackbars {...snackbars} />}
      <DialogTitle disableTypography>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h3">{title}</Typography>
          <IconButton aria-label="close" size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent style={{ padding: '24px' }}>
        {lockedFeatureFlag === 'cfg_premium_content' && (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
              margin: '4rem 0',
            }}
          >
            <div
              style={{
                width: '250px',
                height: '40px',
                backgroundImage: `url(${cfgLogo})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            />
            <Typography
              variant="h1"
              component="h1"
              color="primary"
              style={{ margin: '.2rem 0', fontSize: '28px', fontWeight: 600 }}
            >
              <Translate id="premium-content.cfg.title" />
            </Typography>
            <Typography
              variant="body2"
              component="body2"
              style={{ fontWeight: 600 }}
            >
              <Translate id="premium-content.cfg.sub-title" />
            </Typography>
          </Box>
        )}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          style={{ margin: '0 1rem 3rem' }}
        >
          {lockedFeatureFlag === 'cfg_premium_content'
            ? message.length > 0 && (
                <Box mt={3}>
                  {features.map((key) => (
                    <Box
                      key={key}
                      display="flex"
                      alignItems="center"
                      mb={2}
                      style={{ fontWeight: 600 }}
                    >
                      <CheckCircleIcon
                        fontSize="small"
                        style={{ marginRight: 8, color: 'white' }}
                      />
                      <Translate id={key} />
                    </Box>
                  ))}
                </Box>
              )
            : message}
        </Box>
        {userCanManageBilling &&
          lockedFeatureFlag === 'cfg_premium_content' &&
          (() => {
            const addonItems = Object.entries(SubscriptionPlansData.addons)
              .map(([key, value]) => {
                const [addon, interval, currency] = key.split('_')

                return {
                  key,
                  addon,
                  interval,
                  currency,
                  amount: value.amount,
                  trialDays: value.trial_days,
                  planUuid: value.plan_uuid,
                }
              })
              .filter(
                (item) => !stripeCurrency || item.currency === stripeCurrency
              )

            const currencies = [...new Set(addonItems.map((i) => i.currency))]
            const hasMultipleCurrencies = currencies.length > 1

            return (
              <>
                {hasMultipleCurrencies && (
                  <Typography
                    variant="body2"
                    style={{
                      marginTop: '1rem',
                      marginBottom: '0.5rem',
                      fontWeight: 500,
                    }}
                  >
                    <Translate id="premium-content.choose-currency" />:
                  </Typography>
                )}
                {addonItems.map((item) => (
                  <Grid item xs={12} key={item.key}>
                    <Button
                      fullWidth
                      disabled={isLoading}
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        handleSubscribeClick(item.planUuid, item.amount)
                      }
                      style={{
                        fontSize: '.9rem',
                        fontWeight: 500,
                        borderRadius: '12px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <span style={{ fontWeight: 800 }}>
                          <Translate id="premium-content.cfg.subscribe" />
                        </span>
                        <span>
                          {item.amount} {item.currency.toUpperCase()}
                        </span>
                      </div>
                    </Button>
                  </Grid>
                ))}
              </>
            )
          })()}
      </DialogContent>
    </Dialog>
  )
}

PremiumContentDialog.defaultProps = {
  lockedFeatureFlag: null,
  stripeCurrency: null,
}

PremiumContentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  lockedFeatureFlag: PropTypes.string,
  stripeCurrency: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({
      seasonId: PropTypes.number,
    }),
  }).isRequired,
  currentClub: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    can: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  team: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
  }).isRequired,
}

export default compose(
  withRouter,
  connect(({ clubs, team, auth }) => ({
    team,
    stripeCurrency: auth.user.stripe_currency,
    currentClub: clubs.current,
  }))
)(PremiumContentDialog)
